import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import '../styles/ContactMessage.css';

function ContactMessage({ 
  contactData, 
  currentUserId, 
  onContactClick,
  isOwn = false 
}) {
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactStatus, setContactStatus] = useState(contactData?.contactStatus || null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  // Privacy-filtered profile data for this contact
  const [privacyProfile, setPrivacyProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Fetch contact status when component mounts
  useEffect(() => {
    if (contactData?.id && currentUserId && !isOwn) {
      fetchContactStatus();
    }
  }, [contactData?.id, currentUserId, isOwn]);

  // Fetch privacy-aware profile (ensures profile photo respects user's privacy settings)
  useEffect(() => {
    let cancelled = false;
    const fetchPrivacyProfile = async () => {
      if (!contactData?.id || !currentUserId) {
        setPrivacyProfile(null);
        return;
      }
      // Avoid refetch if we already have it
      if (privacyProfile && privacyProfile.id === contactData.id) return;
      try {
        setLoadingProfile(true);
        const resp = await axios.get(`${config.apiUrl}/api/auth/profile/${contactData.id}?requestingUserId=${currentUserId}`);
        if (!cancelled) {
          setPrivacyProfile(resp.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch privacy profile for contact share:', err);
          setPrivacyProfile({ profileImage: null });
        }
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    };
    fetchPrivacyProfile();
    return () => { cancelled = true; };
  }, [contactData?.id, currentUserId]);

  const fetchContactStatus = async () => {
    setIsLoadingStatus(true);
    try {
      const response = await axios.get(`${config.apiUrl}/api/contacts/status/${currentUserId}/${contactData.id}`);
      setContactStatus(response.data.contactStatus);
    } catch (error) {
      console.error('Error fetching contact status:', error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleAddContact = async () => {
    if (!contactData?.username || isAddingContact) return;

    setIsAddingContact(true);
    try {
      await axios.post(`${config.apiUrl}/api/contacts/request`, {
        requesterId: currentUserId,
        recipientUsername: contactData.username
      });
      
      setContactStatus('pending');
      // Could show a toast notification here
    } catch (error) {
      console.error('Error sending contact request:', error);
      // Could show error message
    } finally {
      setIsAddingContact(false);
    }
  };

  const handleChatClick = () => {
    if (contactData?.id && onContactClick) {
      onContactClick(contactData.id);
    }
  };

  const getStatusButton = () => {
    if (isOwn) return null; // Don't show buttons for own contact shares
    
    if (isLoadingStatus) {
      return (
        <button className="contact-action-btn loading-btn" disabled>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
            <path d="M21 12a9 9 0 11-6.219-8.56"/>
          </svg>
          Loading...
        </button>
      );
    }
    
    switch (contactStatus) {
      case 'accepted':
        return (
          <button className="contact-action-btn chat-btn" onClick={handleChatClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            Chat
          </button>
        );
      case 'pending':
        return (
          <button className="contact-action-btn pending-btn" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Request Sent
          </button>
        );
      case 'rejected':
      default:
        return (
          <button 
            className="contact-action-btn add-btn" 
            onClick={handleAddContact}
            disabled={isAddingContact}
          >
            {isAddingContact ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                <path d="M21 12a9 9 0 11-6.219-8.56"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14"/>
                <path d="M5 12h14"/>
              </svg>
            )}
            Add Contact
          </button>
        );
    }
  };

  if (!contactData) {
    return (
      <div className="contact-message error">
        <div className="contact-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <div className="contact-details">
          <div className="contact-name">Contact unavailable</div>
          <div className="contact-info">This contact could not be loaded</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`contact-message ${isOwn ? 'own' : ''}`}>
      <div className="contact-avatar">
        {(() => {
          // Until privacy data loads, show placeholder to avoid flashing a photo that might be private.
            const profileImage = privacyProfile?.profileImage || null;
            if (profileImage) {
              return (
                <img
                  src={`${config.apiUrl}${profileImage}`}
                  alt={contactData.username}
                  className="contact-profile-image"
                />
              );
            }
            return (
              <div className="contact-avatar-placeholder notranslate">
                {contactData.username.charAt(0).toUpperCase()}
              </div>
            );
        })()}
      </div>
      
      <div className="contact-details">
        <div className="contact-name notranslate">{contactData.username}</div>
        <div className="contact-info">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Contact
        </div>
      </div>
      
      <div className="contact-actions">
        {getStatusButton()}
      </div>
    </div>
  );
}

export default ContactMessage;
